<script lang="ts" setup>
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { type TextMessage } from './TextMessagesTypes';
    import { vTooltip } from '@shared/directives';
    import { formatNumericText } from '@cwd/Shared/utils/formatNumericText';
    import Loading from '@shared/components/Loading.vue';
    import { type status as Tab } from './List.vue';

    const props = defineProps<{
        textMessage: TextMessage;
        currentTab?: Tab;
    }>();

    const draftStatus = 10;
    const sentStatus = 3;
</script>

<script lang="ts"></script>

<template>
    <div class="flex-1" style="min-width: min(400px, 100%)">
        <h2 class="h4 item-heading">
            <a :href="'/TextMessages/' + textMessage.SmsMessageId + (currentTab === 'sent' ? '/Statistics' : '')">
                {{ textMessage.Subject || $Resources.NoTitleLabel }}
            </a>
            <small>{{ textMessage.Category.Name }}</small>
            <a
                v-if="textMessage.Event"
                class="mini-label mini-label-info m-xxs-l"
                rel="noopener"
                target="_blank"
                :href="'/Events/' + textMessage.Event.EventId"
                v-tooltip.top="textMessage.Event.Name">
                {{ $Resources.AssociatedEventLabel }}
            </a>
            <a
                v-if="textMessage.Journey"
                class="mini-label mini-label-info m-xxs-l"
                rel="noopener"
                target="_blank"
                :href="'/journeys/' + textMessage.Journey.JourneyId"
                v-tooltip.top="textMessage.Journey.Title">
                Journey
            </a>
        </h2>

        <p v-if="textMessage.Event">
            <strong>{{ $Resources.IssueEventHeading }}:</strong>
            {{ textMessage.Event.Name }}
            <span v-if="textMessage.Event.Start">
                {{ '(' + formatDateTime(textMessage.Event.Start) + ')' }}
            </span>
        </p>

        <p class="meta">
            <span class="glyphicon glyphicon-time"></span>
            {{ $Resources.GenericCreatedLabel }}
            <time :datetime="textMessage.Created">{{ formatDateTime(textMessage.Created) }}</time>
            {{ $Resources.GenericByLabel }}
            {{ textMessage.CreatedByUser.Name }}

            <i class="fa fa-pencil" aria-hidden="true"></i>
            {{ $Resources.LastModifiedLabel }}
            <time :datetime="textMessage.Modified">{{ formatDateTime(textMessage.Modified) }}</time>
            {{ $Resources.GenericByLabel }}
            {{ textMessage.ModifiedByUser.Name }}
        </p>
    </div>
    <div class="align-self-center" v-if="currentTab === 'scheduled'">
        <p class="d-flex flex-wrap gap-sm align-items-baseline m-xs-b small">
            {{ $Resources.SmsScheduledForSendingLabel }}
            {{
                formatDateTime(textMessage.ScheduledForSending, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                })
            }}
            {{ $Resources.SmsScheduledForSendingLabelklockan }}
            {{
                formatDateTime(textMessage.ScheduledForSending, {
                    hour: 'numeric',
                    minute: 'numeric',
                })
            }}

            <button type="button" class="btn btn-default btn-sm" @click="$emit('update:status', { id: textMessage.SmsMessageId, status: draftStatus })">
                {{ $Resources.SmsAbortScheduledButton }}
            </button>
        </p>
    </div>
    <div class="align-self-center" v-if="currentTab === 'paused'">
        <div class="d-flex flex-wrap gap-sm m-xs-b">
            <button type="button" class="btn btn-default btn-sm" @click="$emit('update:status', { id: textMessage.SmsMessageId, status: draftStatus })">
                <i class="fa fa-chevron-left" aria-hidden="true"></i>
                {{ $Resources.MoveIssueToDraftsButton }}
            </button>

            <button type="button" class="btn btn-default btn-sm" @click="$emit('update:status', { id: textMessage.SmsMessageId, status: sentStatus })">
                {{ $Resources.MoveIssueToSentButton }}
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
        </div>
    </div>
    <div class="align-self-center" v-if="currentTab === 'sending'">
        <Loading :show="true" :loadingStyle="'progress-bar'"></Loading>
        <p class="d-flex flex-wrap gap-sm align-items-baseline m-xs-b small">
            {{ $Resources.SmsSendingLabel }}
            <button type="button" class="btn btn-default btn-sm" @click="$emit('abortSending', textMessage.SmsMessageId)">
                {{ $Resources.SmsAbortSendingButton }}
            </button>
        </p>
    </div>
    <div v-if="currentTab === 'sending'" class="ug-bars text-center">
        <div class="bar-label">
            {{ $Resources.SmsSentLabel }}
            <span>
                {{
                    formatNumericText(textMessage.SentCount, { precision: 1, largeNumbers: true }) +
                    '/' +
                    formatNumericText(textMessage.RecipientCount, {
                        precision: 1,
                        largeNumbers: true,
                    })
                }}
            </span>
        </div>
        <div class="bar">
            <span
                class="bar-value bg-sent"
                :style="{
                    width: (textMessage.SentCount / textMessage.RecipientCount) * 100 + '%',
                }"></span>
        </div>
    </div>
    <div v-if="currentTab === 'sent'">
        <div class="d-flex col-gap-lg flex-wrap ug-bars text-center">
            <div>
                <div class="bar-label">
                    {{ $Resources.SmsSentLabel }}
                    <span>
                        {{
                            formatNumericText(textMessage.RecipientCount, {
                                precision: 1,
                                largeNumbers: true,
                            })
                        }}
                    </span>
                </div>
                <div class="bar">
                    <span class="bar-value bg-sent" :style="{ width: formatNumericText(textMessage.SentPercentage) + '%' }"></span>
                </div>
            </div>

            <div>
                <div class="bar-label">
                    {{ $Resources.SmsPendingLabel }}
                    <span>{{ formatNumericText(textMessage.PendingPercentage) }}%</span>
                </div>
                <div class="bar">
                    <span class="bar-value pending" :style="{ width: formatNumericText(textMessage.PendingPercentage) + '%' }"></span>
                </div>
            </div>

            <div>
                <div class="bar-label">
                    {{ $Resources.SmsReceivedPercentageLabel }}
                    <span>{{ formatNumericText(textMessage.ReceivedPercentage, { precision: 0 }) }}%</span>
                </div>
                <div class="bar">
                    <span class="bar-value bg-received" :style="{ width: formatNumericText(textMessage.ReceivedPercentage) + '%' }"></span>
                </div>
            </div>

            <div>
                <div class="bar-label">
                    {{ $Resources.IssuesBouncedPercentageLabel }}
                    <span v-if="textMessage.BouncedPercentage > 0 && textMessage.BouncedPercentage < 1">
                        {{ formatNumericText(textMessage.BouncedPercentage, { precision: 1 }) }}%
                    </span>
                    <span v-if="textMessage.BouncedPercentage == 0 || textMessage.BouncedPercentage > 1">
                        {{ formatNumericText(textMessage.BouncedPercentage, { precision: 0 }) }}%
                    </span>
                </div>
                <div class="bar">
                    <span class="bar-value bg-bounced" :style="{ width: formatNumericText(textMessage.BouncedPercentage) + '%' }"></span>
                </div>
            </div>
        </div>
    </div>
</template>

<style></style>
